import Stack from '@mui/material/Stack';
import type { ParseKeys } from 'i18next';
import { Suspense, lazy } from 'react';
import { DebugLevel } from '../../_configs/types';
import { useSettingsContext } from '../../context/settings';
import useLocales from '../../hooks/useLocales';
import { SourceList } from '../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import CarTileListMini from '../car-tile/car-tile-list-mini';
import CarTileListMiniSkeleton from '../car-tile/car-tile-list-mini-skeleton';
import ContactVendor from '../contact-form/ContactVendor';
import FormProvider from '../hook-form/form-provider/component';
import Iconify from '../iconify/component';
import type { ContactDialogProps } from './types';

const LoadingButton = lazy(() => import('@mui/lab/LoadingButton' /* webpackChunkName: "Mui_LoadingButton" */));
const Alert = lazy(() => import('@mui/material/Alert' /* webpackChunkName: "Mui_Alert" */));
const Button = lazy(() => import('@mui/material/Button' /* webpackChunkName: "Mui_Button" */));
const Dialog = lazy(() => import('@mui/material/Dialog' /* webpackChunkName: "Mui_Dialog" */));
const DialogActions = lazy(() => import('@mui/material/DialogActions' /* webpackChunkName: "Mui_DialogActions" */));
const DialogContent = lazy(() => import('@mui/material/DialogContent' /* webpackChunkName: "Mui_DialogContent" */));
const DialogTitle = lazy(() => import('@mui/material/DialogTitle' /* webpackChunkName: "Mui_DialogTitle" */));
const Divider = lazy(() => import('@mui/material/Divider' /* webpackChunkName: "Mui_Divider" */));

export default function ContactDialog(props: ContactDialogProps) {
    const { carSelectDialog, defaultValues, isProbefahrt, loading, methods, module, onSubmit, openContactForm, openCarSelector, rootContainer, values } = props;

    const { translate } = useLocales();
    const settings = useSettingsContext();

    const {
        formState: { errors, isSubmitting },
        handleSubmit,
        reset,
        setValue,
    } = methods;

    return (
        <Suspense fallback={<div />}>
            <Dialog
                data-testid="UseContact_Dialog"
                open
                onClose={() => {
                    // Preserve values for accessoires & accessoiresPrice when Dialog is closed
                    reset({
                        ...defaultValues,
                        accessoires: values?.accessoires,
                        accessoiresPrice: values?.accessoiresPrice,
                    });
                    openContactForm?.onFalse();
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ [getStyleQuery()]: { pb: '0 !important' } }}>
                    {!checkUndefinedNullOrEmpty(values?.car?.loc?.name)
                        ? `${translate(`common:carView.contact.requestTypes.${values?.event}a` as ParseKeys<'common'>)} ${values?.car?.loc?.name}`
                        : translate(`common:carView.contact.requestTypes.${values?.event}` as ParseKeys<'common'>)}
                </DialogTitle>

                <DialogContent>
                    <FormProvider formKey={`${module}_UseContact_Dialog_FormProvider`} methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Stack gap={2} sx={{ mt: 2 }}>
                            <Suspense fallback={<CarTileListMiniSkeleton rootContainer={rootContainer} settings={settings} />}>
                                <CarTileListMini
                                    accessoiresPrice={values?.accessoiresPrice > 0 ? values?.accessoiresPrice : undefined}
                                    car={values?.car}
                                    disabled={!checkUndefinedNullOrEmpty(values?.afterSubmit) || loading || !checkUndefinedNullOrEmpty(values?.car)}
                                    isProbefahrt={isProbefahrt}
                                    loading={loading}
                                    module={module}
                                    onMiniClick={openCarSelector.onTrue}
                                    showMonthlyRate={!checkUndefinedNullOrEmpty(values?.fd) ? { type: values?.fd?.type, rate: values?.fd?.monthlyRate } : undefined}
                                    sourceIndex={0}
                                    sourceList={SourceList?.Contact}
                                />
                                {carSelectDialog}
                            </Suspense>
                            {!!errors?.car?.id && (
                                <Alert severity="error" sx={{ [getStyleQuery()]: { whiteSpace: 'pre-wrap' } }}>
                                    {errors?.car?.id?.message}
                                </Alert>
                            )}
                            <Divider id="usecontact_dialog_divider" orientation="horizontal" sx={{ [getStyleQuery()]: { borderStyle: 'dashed', my: 1 } }} />
                            {!checkUndefinedNullOrEmpty(values?.afterSubmit) ? (
                                <Alert data-testid="UseContact_Dialog_Success" severity="success" sx={{ [getStyleQuery()]: { whiteSpace: 'pre-wrap' } }}>
                                    {values.afterSubmit}
                                </Alert>
                            ) : (
                                // Contact Form
                                <ContactVendor isProbefahrt={isProbefahrt} setValue={setValue} values={values} />
                            )}
                            {!!errors.afterSubmit && (
                                <Alert data-testid="UseContact_Dialog_Error" severity="error" sx={{ [getStyleQuery()]: { whiteSpace: 'pre-wrap' } }}>
                                    {translate('common:carView.contact.tryAgain')}
                                    {settings?.general?.debug >= DebugLevel.ERRORS ? '\n' + errors.afterSubmit.message : null}
                                </Alert>
                            )}
                        </Stack>
                    </FormProvider>
                </DialogContent>

                <DialogActions>
                    <Stack direction="row" justifyContent="space-between" width="100%">
                        <Button
                            color="primary"
                            disabled={isSubmitting}
                            variant="soft"
                            id="detailview2_dialog_button_close"
                            data-testid="UseContact_Dialog_Close"
                            startIcon={<Iconify icon="mdi:close" />}
                            onClick={() => {
                                // Preserve values for accessoires & accessoiresPrice when Dialog is closed
                                reset({
                                    ...defaultValues,
                                    accessoires: values?.accessoires,
                                    accessoiresPrice: values?.accessoiresPrice,
                                });
                                openContactForm?.onFalse();
                            }}
                            sx={{
                                [getStyleQuery()]: {
                                    '&:hover': { borderColor: (theme) => theme.palette.primary.main, boxShadow: '0 0 0 0.5px (theme) => theme.palette.primary.main' },
                                },
                            }}
                        >
                            {translate('common:buttons.close')}
                        </Button>
                        {checkUndefinedNullOrEmpty(values?.afterSubmit) && (
                            <LoadingButton
                                id="detailview2_dialog_button_send"
                                data-testid="UseContact_Dialog_Submit"
                                loading={isSubmitting}
                                variant="contained"
                                startIcon={<Iconify icon="mdi:send" />}
                                onClick={handleSubmit(onSubmit)}
                                color="primary"
                            >
                                {translate('common:carView.contact.send')}
                            </LoadingButton>
                        )}
                    </Stack>
                </DialogActions>
            </Dialog>
        </Suspense>
    );
}
